import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setForm(data);
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        allocated_capital: parseFloat(form.allocated_capital),
        risk_fraction: parseFloat(form.risk_fraction),
        daily_loss_fraction: parseFloat(form.daily_loss_fraction),
        premium_deployment_fraction: parseFloat(form.premium_deployment_fraction),
        max_lots: parseInt(form.max_lots),
        max_trades_per_day: parseInt(form.max_trades_per_day),
        reward_to_risk: parseFloat(form.reward_to_risk),
        stop_atr_multiple: parseFloat(form.stop_atr_multiple),
        ml_gating_enabled: !!form.ml_gating_enabled,
        ml_gating_threshold: parseFloat(form.ml_gating_threshold),
      };
      await api.put("/settings", payload);
      toast.success("Settings updated");
      load();
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const clearDemo = async () => {
    try {
      await api.post("/demo/clear");
      toast("Demo data cleared");
    } catch (e) {
      toast.error("Failed to clear demo data");
    }
  };

  if (!settings) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="settings-page">
      <h1 className="text-2xl font-medium tracking-tight">Settings</h1>
      <p className="text-xs text-slate-500 uppercase tracking-[0.15em]">
        Risk parameters, environment &amp; disclosures
      </p>

      <div className="surface rounded-sm p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Risk Parameters</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["allocated_capital", "Allocated Capital (₹)", "number", 1000],
            ["risk_fraction", "Risk fraction / trade", "number", 0.001],
            ["daily_loss_fraction", "Daily loss limit", "number", 0.001],
            ["premium_deployment_fraction", "Max premium deployed", "number", 0.01],
            ["max_lots", "Max lots per trade", "number", 1],
            ["max_trades_per_day", "Max trades / day", "number", 1],
            ["reward_to_risk", "Reward-to-risk", "number", 0.1],
            ["stop_atr_multiple", "Stop = ATR ×", "number", 0.1],
          ].map(([k, label, type, step]) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</label>
              <input
                data-testid={`settings-${k}`}
                type={type}
                step={step}
                value={form[k] ?? ""}
                onChange={(e) => update(k, e.target.value)}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none"
              />
            </div>
          ))}
        </div>
        <div className="mt-6 flex gap-3">
          <button
            data-testid="settings-save-btn"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>

      <div className="surface rounded-sm p-6" data-testid="ml-gating-card">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
            ML Gating (Shadow → Filter)
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              data-testid="ml-gating-toggle"
              type="checkbox"
              checked={!!form.ml_gating_enabled}
              onChange={(e) => update("ml_gating_enabled", e.target.checked)}
              className="h-4 w-4 accent-blue-500"
            />
            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-300">
              {form.ml_gating_enabled ? "Enabled" : "Disabled"}
            </span>
          </label>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed mb-4">
          When enabled, deterministic signals with{" "}
          <code className="text-blue-300">ml_probability</code> below the
          threshold are auto-rejected with code{" "}
          <code className="text-amber-300">ML_GATE_REJECTED</code>. Compare
          win-rate with/without to quantify the ML filter&#39;s edge.
        </p>
        <div className="flex items-center gap-4">
          <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 w-40">
            Probability Threshold
          </label>
          <input
            data-testid="ml-gating-threshold-slider"
            type="range"
            min="0.5"
            max="0.95"
            step="0.01"
            value={form.ml_gating_threshold ?? 0.55}
            onChange={(e) => update("ml_gating_threshold", parseFloat(e.target.value))}
            className="flex-1 accent-blue-500"
            disabled={!form.ml_gating_enabled}
          />
          <span
            data-testid="ml-gating-threshold-value"
            className="font-mono text-sm text-blue-300 w-16 text-right"
          >
            {(form.ml_gating_threshold ?? 0.55).toFixed(2)}
          </span>
        </div>
        <div className="mt-4 flex gap-3">
          <button
            data-testid="ml-gating-save-btn"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save ML Gate"}
          </button>
        </div>
      </div>

      <div className="surface rounded-sm p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Environment</div>
        <div className="grid grid-cols-2 gap-4 text-xs font-mono">
          <div><span className="text-slate-500">Live trading enabled:</span> <span className={settings.live_trading_enabled ? "text-pos" : "text-slate-300"}>{String(settings.live_trading_enabled)}</span></div>
          <div><span className="text-slate-500">Demo mode:</span> <span className="text-amber-400">{String(settings.demo_mode)}</span></div>
        </div>
      </div>

      <div className="surface rounded-sm p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Demo Data</div>
        <button
          data-testid="clear-demo-btn"
          onClick={clearDemo}
          className="px-4 py-2 border border-red-800 text-red-400 hover:bg-red-950 text-xs uppercase tracking-[0.15em] rounded-sm flex items-center gap-2"
        >
          <Trash2 size={12} /> Clear Demo Data
        </button>
      </div>

      <div className="surface rounded-sm p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Telegram Alerts (free)</div>
        <p className="text-xs text-slate-400 leading-relaxed mb-3">
          Get a daily P&amp;L summary at 15:15 IST. Set <code className="text-blue-300">TELEGRAM_BOT_TOKEN</code> and <code className="text-blue-300">TELEGRAM_CHAT_ID</code> in backend .env. Create a bot via <code className="text-slate-300">@BotFather</code>, get your chat id from <code className="text-slate-300">@userinfobot</code>.
        </p>
        <div className="mb-3 p-3 rounded-sm bg-slate-900/60 border border-slate-800 text-[11px] text-slate-300 leading-relaxed">
          <div className="text-blue-300 mb-1">Bot connected: <code>t.me/MyEdgeTraderBot</code></div>
          Send <code className="text-slate-100">/start</code> to your bot from your phone, then click{" "}
          <span className="text-blue-300">Detect Chat ID</span> below. Paste the returned id into{" "}
          <code className="text-blue-300">TELEGRAM_CHAT_ID</code> in <code>/app/backend/.env</code>{" "}
          and restart the backend to activate alerts.
        </div>
        <button
          data-testid="telegram-detect-btn"
          onClick={async () => {
            try {
              const { data } = await api.post("/notify/detect-chat-id");
              toast.success(`Chat ID: ${data.chat_id} — paste into backend .env`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Detect failed");
            }
          }}
          className="mr-2 px-4 py-2 border border-blue-800 text-blue-300 hover:bg-blue-950 text-xs uppercase tracking-[0.15em] rounded-sm"
        >
          Detect Chat ID
        </button>
        <button
          data-testid="telegram-test-btn"
          onClick={async () => {
            try {
              await api.post("/notify/test");
              toast.success("Test message sent to Telegram");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Send failed — configure token first");
            }
          }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs uppercase tracking-[0.15em] rounded-sm"
        >
          Send Test Alert
        </button>
        <button
          data-testid="telegram-summary-btn"
          onClick={async () => {
            try {
              await api.post("/notify/daily-summary");
              toast.success("Daily summary sent");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Send failed");
            }
          }}
          className="ml-2 px-4 py-2 border border-slate-700 hover:bg-slate-800/50 text-xs uppercase tracking-[0.15em] rounded-sm"
        >
          Send Today's Summary Now
        </button>
        <button
          data-testid="telegram-hourly-btn"
          onClick={async () => {
            try {
              await api.post("/notify/hourly-digest");
              toast.success("Hourly digest sent");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Send failed");
            }
          }}
          className="ml-2 px-4 py-2 border border-slate-700 hover:bg-slate-800/50 text-xs uppercase tracking-[0.15em] rounded-sm"
        >
          Send Hourly Digest
        </button>
        <button
          data-testid="telegram-weekend-btn"
          onClick={async () => {
            try {
              await api.post("/notify/weekend-digest");
              toast.success("Weekend digest sent");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Send failed");
            }
          }}
          className="ml-2 px-4 py-2 border border-slate-700 hover:bg-slate-800/50 text-xs uppercase tracking-[0.15em] rounded-sm"
        >
          Send Weekend Recap
        </button>
        <button
          data-testid="telegram-weekend-preview-btn"
          onClick={async () => {
            try {
              const { data } = await api.get("/notify/weekend-digest/preview");
              toast.message("Weekend recap preview", {
                description: data?.preview || "(empty)",
                duration: 15000,
              });
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Preview failed");
            }
          }}
          className="ml-2 px-4 py-2 border border-slate-700 hover:bg-slate-800/50 text-xs uppercase tracking-[0.15em] rounded-sm"
        >
          Preview Weekend Recap
        </button>
        <p className="mt-3 text-[10px] text-slate-500 leading-relaxed">
          Automatic schedule (Mon–Fri): hourly digest at 09:30, 10:30, 11:30, 12:30, 13:30, 14:30 IST · daily summary at 15:15 IST · <span className="text-slate-300">weekly recap Sunday 18:00 IST</span>.
        </p>
      </div>

      <div className="surface rounded-sm p-6 border-l-2 border-l-amber-500">
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-2">Risk Disclosure</div>
        <p className="text-xs text-slate-300 leading-relaxed">
          Trading involves substantial risk of loss and is not suitable for every investor. The paper-trading results
          displayed in this application do not guarantee future live-trading results. Broker charges, slippage, and
          liquidity conditions in real markets may differ materially from paper estimates.
        </p>
      </div>
    </div>
  );
}
